/**
 * toSimpleRounds - Convert ScriptItem[] to SimplePlayer's Round[] format
 *
 * This is the only conversion layer needed. No RoundBuilder. No roundAdapter.
 *
 * generateLearningScript() → toSimpleRounds() → SimplePlayer.initialize()
 *
 * Pause duration: see computePauseDuration.ts — single helper driven by the
 * admin-controlled ModeConfig (algorithm_config table). The baked value below
 * uses DEFAULT_FAST as a fallback; LearningPlayer's runtime override
 * recomputes from the live config so admin tweaks affect both the visible
 * countdown and the actual setTimeout in lockstep.
 */

import type { ScriptItem } from './generateLearningScript'
import type { Round, Cycle } from '../playback/SimplePlayer'
import { computePauseDuration } from '../playback/computePauseDuration'
import { DEFAULT_FAST } from '../composables/useAlgorithmConfig'
import { reportIntroAudioMissing } from '../playback/introAudioTelemetry'
import { capRoundCycles, cyclePromptIdentity } from '../playback/capConsecutiveRepeats'

import { apiUrl } from '@/platform/apiBase'
import {
  playbackSpeedForVoice,
  isHumanVoicedCourse,
  type CourseVoicePace, type PaceMode,
} from '@ssi/core'

const audioUrl = (uuid: string | undefined): string => {
  if (!uuid) return ''
  return apiUrl(`/api/audio/${uuid}`)
}

/** Tom 2026-10-18: mode alone controls the recording multiplier.
 * Historic fields remain accepted for bundle/API compatibility and are ignored.
 */
export interface TargetSpeedConfig {
  mode?: PaceMode
  globalSpeed?: number
  nativeSpeed?: boolean
  introSpeed?: number
  firstReviewSpeed?: number
  reviewSpeed?: number
  rampSeeds?: number
  rampStartSpeed?: number
  voicePace?: CourseVoicePace | null
  /** Course being played: a human-voiced language (Welsh) is never slowed (Tom 2026-10-18). */
  courseCode?: string | null
  courseVoiceSpeed?: { target1?: number | null; target2?: number | null } | null
  beltRamp?: boolean
}

// Tom 2026-10-18: retained API; course voice overrides are retired.
export function courseVoiceSpeedFor(_config: TargetSpeedConfig, _slot: 'target1' | 'target2'): number | null {
  return null
}

/**
 * THE RETIRED BELT RAMP — white 0.8 → yellow 0.9 → orange 0.95 → green 1.0.
 *
 * Tom, 2026-08-29 (plate S-345): "We also have a FAST setting — which could
 * perhaps be a flat 0.9x … Then we can dispense with the belt ramp chicanery?"
 * Speed is now a function of ROLE and MODE, never of belt or seed, and
 * `computeCycleSpeed` no longer calls this.
 *
 * It survives as an exported function for exactly one reason: the LISTENING
 * tests still use it to assert that the listening path never took a belt term
 * and still doesn't. Nothing in the SPEAKING path may call it again.
 *
 * @deprecated The belt ramp is retired. Do not reintroduce it into any speed path.
 */
export function beltSpeed(seedNumber: number): number {
  if (seedNumber < 8) return 0.8    // White  (seeds 1-7)
  if (seedNumber < 20) return 0.9   // Yellow (seeds 8-19)
  if (seedNumber < 40) return 0.95  // Orange (seeds 20-39)
  return 1.0                        // Green+ (seeds 40+)
}

/** Tom 2026-10-18: flat multiplier on every recording, regardless of voice. */
export function computeCycleSpeed(
  _seedNumber: number, config: TargetSpeedConfig, slot: 'target1' | 'target2' = 'target1'
): number {
  return playbackSpeedForVoice(null, slot, config.mode ?? 'easy', config.courseCode).speed
}

export function explainCycleSpeed(
  config: TargetSpeedConfig, slot: 'target1' | 'target2' = 'target1'
): { speed: number; voiceId: string | null; measured: boolean; reason: string } {
  const decision = playbackSpeedForVoice(null, slot, config.mode ?? 'easy')
  return { speed: decision.speed, voiceId: null, measured: false, reason: decision.reason }
}

/**
 * EASY-MODE LISTENING PACE — 1.0×, i.e. no Easy-specific slowing at all.
 *
 * Tom, 2026-08-10, testing listening live: "'Easy' seems to have slowed the
 * conversations in the listening section - I don't think we want to be doing
 * that"; "I think we can return the default listening speed settings to 1.0x on
 * EASY, I think we moved them to 0.8x." That superseded his T-13 ruling of
 * 2026-08-07 (Easy listening at 0.8×), which had shipped as `0.8` here.
 *
 * Since 2026-08-16 nothing in `computeListeningSpeed` reads a mode at all —
 * listening is never slowed for anyone (see that function). The constant
 * survives as the Dialogues overlay's opening speed, its one remaining
 * consumer, where it states the same thing: Easy opens at full pace.
 */
export const EASY_LISTENING_SPEED = 1.0

// Tom 2026-10-18: listening plays the recording at 1.0.
export function computeListeningSpeed(
  _roleSpeed: number, _seedNumber: number, _config: TargetSpeedConfig
): number { return 1.0 }

/**
 * Generator core shared by the sync and cooperative converters. Yields a
 * checkpoint per round so the cooperative wrapper can hand the main thread
 * back mid-conversion (post-READY interactivity, founder 2026-07-30) while
 * the sync wrapper drains it in one pass, byte-identical to the old code.
 */
function* toSimpleRoundsGen(
  items: ScriptItem[],
  targetSpeed: TargetSpeedConfig
): Generator<void, Round[], void> {
  // Group by roundNumber - each round is a complete learning unit
  // Items within a round share the same roundNumber, but may have different legoKeys
  // (e.g., spaced_rep items review older LEGOs but belong to the current round)
  const byRound = new Map<number, ScriptItem[]>()
  for (const item of items) {
    const key = item.roundNumber
    if (!byRound.has(key)) {
      byRound.set(key, [])
    }
    byRound.get(key)!.push(item)
  }

  const rounds: Round[] = []

  for (const [roundNum, roundItems] of byRound.entries()) {
    yield
    // Find the intro item to get the primary LEGO for this round
    const introItem = roundItems.find(i => i.type === 'intro')
    const primaryLegoKey = introItem?.legoKey || roundItems[0]?.legoKey || ''
    const primarySeedId = introItem?.seedId || roundItems[0]?.seedId || ''

    // Build cycles — intros always included (define round structure),
    // listening items only need target audio, other items need all three audio IDs
    const cycles: Cycle[] = []
    let skippedNoAudio = 0
    // Default pace depends only on course/mode, not the item. Resolve lazily
    // per voice and round; reset after each cooperative checkpoint so mode
    // changes between rounds still take effect.
    let defaultSpeed: number | undefined
    let defaultSpeed2: number | undefined
    // Explicit slow rates share the course policy within this round too.
    let humanVoiced: boolean | undefined
    for (const i of roundItems) {
      if (i.type === 'listening') {
        // Listening cycles now carry one audio per cycle (one playlist
        // entry: ps/ps2x → target1Id, trans → knownAudioId). Either is
        // acceptable; missing both means a stale row to skip.
        if (!i.knownAudioId && !i.target1Id) { skippedNoAudio++; continue }
      } else if (i.type === 'component_intro') {
        if (!i.target1Id) { skippedNoAudio++; continue }
      } else if (i.type === 'listen_intro' || i.type === 'listen_outro') {
        // Bookends play one known-language clip with no target voices.
        if (!i.knownAudioId) { skippedNoAudio++; continue }
      } else if (i.type === 'pod') {
        // Pod plays carry exactly one of {knownAudioId (translation play),
        // target1Id (target play at slow/fast/2× via playbackSpeed)}.
        if (!i.knownAudioId && !i.target1Id) { skippedNoAudio++; continue }
      } else if (i.type !== 'intro') {
        if (!i.knownAudioId || !i.target1Id || !i.target2Id) { skippedNoAudio++; continue }
      }

      // Intro/component_intro: use presentationAudioId as prompt audio
      // ("The Spanish for 'want', as in 'I want to learn', is:")
      // Regular items: use knownAudioId (the known-language prompt)
      const isIntroLike = i.type === 'intro' || i.type === 'component_intro'
      const promptAudioId = isIntroLike
        ? (i.presentationAudioId || i.knownAudioId)
        : i.knownAudioId
      // Intro only: the known clip stands behind the narration, for the case
      // the narration id exists but cannot be played (a dangling id 404s and
      // the element refuses the JSON body — job #256). SimplePlayer falls back
      // to this instead of skipping the prompt.
      const promptFallbackId = isIntroLike && i.presentationAudioId ? i.knownAudioId : undefined

      // The fallback point. Until 2026-08-04 this branch was silent in both
      // senses: it quietly degraded to known audio (or to nothing), and it
      // emitted no telemetry, so a course-wide presentation-audio gap was
      // invisible until a learner reported it. See introAudioTelemetry.ts.
      if (isIntroLike && !i.presentationAudioId) {
        reportIntroAudioMissing({
          legoId: i.legoKey,
          cycleId: i.uuid,
          cycleType: i.type,
          tier: i.knownAudioId ? 'known_fallback' : 'silent',
          source: 'script',
        })
      }

      // Target speed: explicit listening/pod rate, otherwise the flat mode multiplier.
      const explicit = i.playbackSpeed
      // A human-voiced language is never slowed, even by an explicit listening/pod
      // rate below 1.0 (Tom, 2026-10-18); a rate above 1.0 is a deliberate stretch.
      // Default rates already enforce this policy; explicit rates >= 1 need no check.
      const speed = explicit !== undefined ? (explicit < 1 && (humanVoiced ??= isHumanVoicedCourse(targetSpeed.courseCode)) ? 1.0 : explicit) : (defaultSpeed ??= computeCycleSpeed(0, targetSpeed))
      // Slot 2 only when it genuinely differs — an explicit override owns both
      // slots (pods play one clip), and an equal number is noise on the wire.
      const speed2 = explicit === undefined
        ? (defaultSpeed2 ??= computeCycleSpeed(0, targetSpeed, 'target2'))
        : undefined

      const isBookend = i.type === 'listen_intro' || i.type === 'listen_outro'
      const isPod = i.type === 'pod'

      cycles.push({
        id: i.uuid,
        type: i.type,
        legoId: i.legoKey,
        known: {
          text: i.knownText,
          audioUrl: audioUrl(promptAudioId),
          ...(promptFallbackId ? { fallbackUrl: audioUrl(promptFallbackId) } : {})
        },
        target: {
          // Bookends carry no target text/audio — SimplePlayer's voice1/voice2
          // phases gracefully skip when URLs are empty.
          // Pods: target1 holds the play audio for slow/fast/2× (with
          // playbackSpeed); voice2 always empty (single-play cycle).
          text: i.targetText,
          ...(i.targetTextNative ? { textNative: i.targetTextNative } : {}),
          voice1Url: isBookend ? '' : audioUrl(i.target1Id),
          voice2Url: (isBookend || isPod) ? '' : audioUrl(i.target2Id)
        },
        // Expose raw target durations so runtime overrides (the active
        // learning mode) can recompute pauseDuration with their own formula
        // instead of just scaling the baked value.
        ...(speed2 !== undefined && speed2 !== speed ? { voice2PlaybackSpeed: speed2 } : {}),
        ...(i.target1DurationMs ? { target1DurationMs: i.target1DurationMs } : {}),
        ...(i.target2DurationMs ? { target2DurationMs: i.target2DurationMs } : {}),
        // At-most-one-audio-track cycles: lets SimplePlayer suppress its
        // "no audio, skipping" warnings for the phases left empty by design.
        ...((isBookend || isPod || i.type === 'listening') ? { singleAudio: true } : {}),
        // Intro/listening/component_intro/bookends/pods: no pause — each
        // sub-cycle carries at most one audio track, chained straight through
        // on 'ended' (no production-recall gap). Other cycles: dynamic pause
        // based on target audio lengths.
        pauseDuration: (i.type === 'intro' || i.type === 'listening' || i.type === 'component_intro' || isBookend || isPod)
          ? 0
          : computePauseDuration(i.target1DurationMs ?? 0, i.target2DurationMs ?? 0, DEFAULT_FAST),
        // Intro/component_intro: linger after voice2 so learner can read
        ...(i.type === 'intro' ? { lingerMs: 2000 } : {}),
        ...(i.type === 'component_intro' ? { lingerMs: 1500 } : {}),
        ...(i.componentLegoIds ? { componentLegoIds: i.componentLegoIds } : {}),
        ...(i.componentLegoTexts ? { componentLegoTexts: i.componentLegoTexts } : {}),
        ...(i.componentLegoTextsNative ? { componentLegoTextsNative: i.componentLegoTextsNative } : {}),
        ...(i.decomposition ? { decomposition: i.decomposition } : {}),
        ...(i.displayTiling ? { displayTiling: i.displayTiling } : {}),
        ...(i.components ? { components: i.components } : {}),
        ...(i.componentsNative ? { componentsNative: i.componentsNative } : {}),
        ...(speed !== 1.0 ? { playbackSpeed: speed } : {})
      })
    }

    if (skippedNoAudio > 0) {
      console.warn(`[toSimpleRounds] Round ${roundNum}: skipped ${skippedNoAudio}/${roundItems.length} items due to missing audio IDs`)
    }
    if (cycles.length === 0) continue

    // Carried from the generator's items; absent on a cached script written
    // before the stamp existed (consumers then fall back to shape).
    const revival = roundItems.find(i => typeof i.revival === 'boolean')?.revival
    rounds.push({
      roundNumber: roundNum,
      legoId: primaryLegoKey,
      seedId: primarySeedId,
      ...(typeof revival === 'boolean' ? { revival } : {}),
      // Canonical LEGO text from intro item — avoids fragile cycle-ID scanning
      ...(introItem ? {
        legoTargetText: introItem.targetText,
        legoKnownText: introItem.knownText,
        ...(introItem.targetTextNative ? { legoTargetTextNative: introItem.targetTextNative } : {})
      } : {}),
      cycles
    })
  }

  // Sort by roundNumber to maintain learning sequence
  rounds.sort((a, b) => a.roundNumber - b.roundNumber)

  // A-64 floor (Tom, 2026-08-06): no mode plays the same prompt more than
  // twice consecutively. generateLearningScript already caps its own output,
  // but this adapter drops cycles whose audio is missing — which can pull two
  // previously separated prompts together — so the last word belongs here,
  // immediately before SimplePlayer receives the rounds.
  return capRoundCycles(rounds, cyclePromptIdentity).rounds
}

export function toSimpleRounds(
  items: ScriptItem[],
  targetSpeed: TargetSpeedConfig = {}
): Round[] {
  const gen = toSimpleRoundsGen(items, targetSpeed)
  for (;;) {
    const step = gen.next()
    if (step.done) return step.value
  }
}

/**
 * Cooperative variant for the ready-gated deferred handoff: identical output
 * to toSimpleRounds, but awaits `yieldTick` at each per-round checkpoint so
 * a whole-course conversion can't hold the main thread past the tick's
 * slice budget (see generateLearningScript's makeSliceYielder).
 */
export async function toSimpleRoundsCooperative(
  items: ScriptItem[],
  targetSpeed: TargetSpeedConfig = {},
  yieldTick?: () => Promise<void>
): Promise<Round[]> {
  const gen = toSimpleRoundsGen(items, targetSpeed)
  for (;;) {
    const step = gen.next()
    if (step.done) return step.value
    if (yieldTick) await yieldTick()
  }
}
